import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from 'components/supabase/supabaseClient';
import DiscountModal from "./DiscountModal";
import Swal from 'sweetalert2';

const TenantDataTable = ({ onTenantSelect, onActionClick }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "healthScore",
    direction: "desc",
  });
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedTenantForDiscount, setSelectedTenantForDiscount] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [tenantsData, setTenantsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🚀 Fetch Tenants from Supabase
  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);

      // 1. Get admins profiles
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id,user_id, name,is_admin, discount_percentage, discount_plans")
        .eq("is_admin", true);

      if (profileError) {
        console.error("Error fetching profiles:", profileError);
        setLoading(false);
        return;
      }

      // 2. For each admin, count users they created
      const tenants = await Promise.all(
        profiles.map(async (profile) => {

          const { count: vendorCount, error: vendorError } = await supabase
            .from("profiles")
            .select("*", { count: "exact", head: true })
            .eq("created_by_id", profile.user_id); 

          if (vendorError) console.error(vendorError);

          // 3. Fetch subscription details for this admin
          const { data: subscription, error: subError } = await supabase
            .from("subscriptions")
            .select(`
              current_period_end,
              plans (
                name,
                yearly_price
              )
            `)
            .eq("user_id", profile.user_id)
            .maybeSingle();

          // const subscription = subscriptions?.[0] || null; // take the latest
          if (subError) console.error(subError);

          // 3. Calculate monthly revenue from purchase_orders
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);

          const { data: orders, error: ordersError } = await supabase
            .from("purchase_orders")
            .select("amount, created_at")
            .eq("user_id", profile.user_id)
            .gte("created_at", startOfMonth.toISOString());

          if (ordersError) console.error(ordersError);

          const monthlyRevenue = orders?.reduce(
            (sum, order) => sum + (order.amount || 0),
            0
          ) || 0;

          // 4. Fetch last activity from Supabase Auth
          // const { data: authUser, error: authError } = await supabase.auth.admin.getUserById(profile.user_id);

          // if (authError) console.error(authError);

          const yearlyPrice = subscription?.plans?.yearly_price ?? null;

          return {
            id: profile.user_id,
            companyName: profile.name || "Unknown Company",
            // logo: profile.avatar_url || "/assets/images/no_image.png",
            subscriptionTier: subscription?.plans?.name || "Trial",
            userCount: vendorCount || 0,
            lastActivity: null,
            renewalDate: subscription?.current_period_end || null,
            healthScore: Math.floor(Math.random() * 30) + 70, 
            discount_percentage: profile.discount_percentage, 
            price: yearlyPrice || 0, 
            monthlyRevenue: monthlyRevenue ? 0.7 * monthlyRevenue : 0,
            status: "active", 
            riskLevel: "low", 
          };
        })
      );

      setTenantsData(tenants);
      setLoading(false);
    };

    fetchTenants();
  }, []);

  // --- Sorting + Filtering
  const filteredAndSortedData = useMemo(() => {
    let filtered = tenantsData.filter(
      (tenant) =>
        tenant.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.subscriptionTier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.status.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "lastActivity" || sortConfig.key === "renewalDate") {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [searchTerm, sortConfig, tenantsData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  // --- Utils
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatLastActivity = (dateString) => {
    if (!dateString) return "Never";
    const now = new Date();
    const activityDate = new Date(dateString);
    const diffInHours = Math.floor((now - activityDate) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTenants.length === paginatedData.length) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(paginatedData.map(tenant => tenant.id));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success';
      case 'trial': return 'bg-warning/10 text-warning';
      case 'suspended': return 'bg-error/10 text-error';
      default: return 'bg-muted text-text-secondary';
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  const applyDiscount = async ({ discount, plans }) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          discount_percentage: Number(discount),
          discount_plans: plans,
        })
        .eq("user_id", selectedTenantForDiscount.id);

      if (error) throw error;

      setTenantsData((prev) =>
        prev.map((tenant) =>
          tenant.id === selectedTenantForDiscount.id
            ? {
                ...tenant,
                discount_percentage: discount,
                discount_plans: plans,
              }
            : tenant
        )
      );

      Swal.fire({
        icon: "success",
        title: "Discount Applied",
        text: `Discount of ${discount}% applied successfully.`,
        confirmButtonColor: "#2563eb",
      });

      setShowDiscountModal(false);
      setSelectedTenantForDiscount(null);

    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.message || "Something went wrong while applying discount",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  const removeDiscount = async (tenant) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Remove Discount?",
      text: "This will remove the discount from this tenant.",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          discount_percentage: null,
          discount_plans: null,
        })
        .eq("user_id", tenant.id);

      if (error) throw error;

      // Update UI state
      setTenantsData((prev) =>
        prev.map((t) =>
          t.id === tenant.id
            ? {
                ...t,
                discount_percentage: null,
                discount_plans: null,
              }
            : t
        )
      );

      Swal.fire({
        icon: "success",
        title: "Discount Removed",
        confirmButtonColor: "#2563eb",
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.message || "Could not remove discount",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary transition-colors ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <div className="flex flex-col">
          <Icon
            name="ChevronUp"
            size={12}
            className={`${
              sortConfig.key === sortKey && sortConfig.direction === 'asc' ?'text-primary' :'text-text-secondary'
            }`}
          />
          <Icon
            name="ChevronDown"
            size={12}
            className={`-mt-1 ${
              sortConfig.key === sortKey && sortConfig.direction === 'desc' ?'text-primary' :'text-text-secondary'
            }`}
          />
        </div>
      </div>
    </th>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-1">
              Tenant Directory
            </h3>
            <p className="text-sm text-text-secondary">
              Comprehensive tenant management and analytics
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <Input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Icon name="Filter" size={16} />
                Filter
              </Button>
              <Button variant="outline" size="sm">
                <Icon name="Download" size={16} />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedTenants.length > 0 && (
          <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-primary">
                {selectedTenants.length} tenant{selectedTenants.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  <Icon name="Mail" size={16} />
                  Send Message
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="Download" size={16} />
                  Export Selected
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="MoreHorizontal" size={16} />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedTenants.length === paginatedData.length && paginatedData.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <SortableHeader label="Company" sortKey="companyName" />
              <SortableHeader label="Subscription" sortKey="subscriptionTier" />
              <SortableHeader label="Users" sortKey="userCount" />
              <SortableHeader label="Last Activity" sortKey="lastActivity" />
              <SortableHeader label="Renewal Date" sortKey="renewalDate" />
              <SortableHeader label="Price" sortKey="healthScore" />
              <SortableHeader label="Discount" sortKey="monthlyRevenue" />
              <SortableHeader label="Status" sortKey="status" />
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {paginatedData.map((tenant) => (
              <tr
                key={tenant.id}
                className="hover:bg-muted/30 transition-colors duration-150 cursor-pointer"
                onClick={() => onTenantSelect(tenant)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedTenants.includes(tenant.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectTenant(tenant.id);
                    }}
                    className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      {tenant.logo ? (
                        <img
                          src={tenant.logo}
                          alt={tenant.companyName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null; // prevent infinite loop
                            e.target.style.display = "none"; // hide broken image
                            e.target.parentNode.querySelector(".initials").style.display = "flex";
                          }}
                        />
                      ) : (
                        <div className="initials w-full h-full flex items-center justify-center bg-gray-200 text-gray-700 font-bold text-xl rounded">
                          {tenant.companyName
                            ?.split(" ")
                            .map((word) => word[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                      )}

                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-primary">
                        {tenant.companyName}
                      </div>
                      <div className="text-xs text-text-secondary">
                        ID: {tenant.id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {tenant.subscriptionTier}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                  {tenant.userCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                  {formatLastActivity(tenant.lastActivity)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                  {formatDate(tenant.renewalDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {tenant.price}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <span className="text-text-primary">
                      %{tenant.discount_percentage ?? 0}
                    </span>

                    {tenant.discount_percentage > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeDiscount(tenant);
                        }}
                        className="text-error hover:text-error/80 transition-colors"
                        title="Remove discount"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    )}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(tenant.status)}`}>
                    {tenant.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTenantForDiscount(tenant);
                        setShowDiscountModal(true);
                      }}
                    >
                      <Icon name="Percent" size={16} />
                    </Button>   
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick('view', tenant);
                      }}
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionClick('more', tenant);
                      }}
                    >
                      <Icon name="MoreHorizontal" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-input border border-border rounded-md px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-text-secondary">
              of {filteredAndSortedData.length} results
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            >
              <Icon name="ChevronLeft" size={16} />
              Previous
            </Button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNumber = i + 1;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`
                      w-8 h-8 rounded-md text-sm font-medium transition-colors
                      ${currentPage === pageNumber
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                      }
                    `}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            >
              Next
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      </div>
      <DiscountModal
        open={showDiscountModal}
        tenant={selectedTenantForDiscount}
        onClose={() => setShowDiscountModal(false)}
        onApply={applyDiscount}
      />
    </div>
  );
};

export default TenantDataTable;